import React from 'react';
import { Button } from 'components';
import Select from 'react-select';
import { TAB_FORM_IS_DIRTY } from 'constants/constants';
import { Accordion, Card, Col, Form, Row } from 'react-bootstrap';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { setSessionStorage } from 'utils/localStorage';
import { Prompt } from 'react-router-dom';
import { useManagerRole } from 'hooks/role';
import { IDealerInfo } from 'slices/auth';
import { ICustomerObject, IDealerOption } from '../Models';

interface IDealerDetailFormProps {
  detailData: ICustomerObject;
  rootDealer?: IDealerInfo;
  validationSchema: any | (() => any);
  onSubmit: (
    values: ICustomerObject,
    formik: FormikHelpers<ICustomerObject>
  ) => void | Promise<any>;
  dealerOptions: IDealerOption[];
  showExtendedButton?: boolean;
  setSaveAndSelect?: (value: boolean) => void;
  handleBack?: () => void;
}

export const DealerCustomersDetailView = ({
  detailData,
  validationSchema,
  rootDealer,
  onSubmit,
  dealerOptions,
  showExtendedButton,
  setSaveAndSelect,
  handleBack,
}: IDealerDetailFormProps) => {
  const { t } = useTranslation();
  const isManager = useManagerRole();

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        initialValues={detailData}
        enableReinitialize
      >
        {({
          handleSubmit,
          handleChange,
          values,
          errors,
          touched,
          dirty,
          setFieldValue,
        }) => {
          setSessionStorage(TAB_FORM_IS_DIRTY, `${dirty}`);
          const isDisabled =
            !isManager && rootDealer?.dealerId !== values.dealerInfo?.dealerId;

          return (
            <>
              <Prompt when={dirty} message={t('unsavedChanges.message')} />
              {Object.keys(errors).length > 0 &&
                Object.entries(errors).map(([key, value]) => (
                  <div key={key} className="alert alert-danger">
                    {value}
                  </div>
                ))}
              <Form
                onSubmit={handleSubmit}
                className="form-control-bordered"
                data-cy="dealer-profile"
              >
                <Row>
                  <Col md={10}>
                    <Form.Group
                      controlId="dealerInfo.dealerId"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('dealerManagement.dealerId')}
                      </Form.Label>
                      <div className="col-sm-7 form-control-bordered padding-zero margin-input">
                        <Select
                          value={dealerOptions.find(
                            (opt) => opt.value === values?.dealerInfo?.dealerId
                          )}
                          onChange={(dealerId: any) => {
                            setFieldValue(
                              'dealerInfo.dealerId',
                              dealerId.value
                            );
                          }}
                          options={dealerOptions}
                          className="select-container bs-select-control"
                          classNamePrefix="select"
                          isDisabled={!isManager}
                          isSearchable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            option: (base) => ({
                              ...base,
                              fontSize: '0.725rem', // Smaller font for dropdown options
                            }),
                          }}
                        />
                      </div>
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.customerId"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.customerId')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.customerId"
                        placeholder={t('customerManagement.customerId')}
                        value={values?.customerInfo.customerId ?? ''}
                        onChange={(e) =>
                          setFieldValue(
                            'customerInfo.customerId',
                            e.target.value,
                            false
                          )
                        }
                        onBlur={handleChange}
                        isInvalid={
                          touched.customerInfo?.customerId &&
                          !!errors.customerInfo?.customerId
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.title"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.title')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.title"
                        placeholder={t('customerManagement.title')}
                        value={values?.customerInfo.title ?? ''}
                        onChange={handleChange}
                        isInvalid={
                          touched.customerInfo?.title &&
                          !!errors.customerInfo?.title
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.salutation"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.salutation')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.salutation"
                        placeholder={t('customerManagement.salutation')}
                        value={values?.customerInfo.salutation ?? ''}
                        onChange={handleChange}
                        isInvalid={
                          touched.customerInfo?.salutation &&
                          !!errors.customerInfo?.salutation
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.firstName"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.firstName')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.firstName"
                        placeholder={t('customerManagement.firstName')}
                        value={values?.customerInfo.firstName ?? ''}
                        onChange={handleChange}
                        isInvalid={
                          touched.customerInfo?.firstName &&
                          !!errors.customerInfo?.firstName
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.lastName"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.lastName')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.lastName"
                        placeholder={t('customerManagement.lastName')}
                        value={values?.customerInfo.lastName ?? ''}
                        onChange={handleChange}
                        isInvalid={!!errors.customerInfo?.lastName}
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.position"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.position')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.position"
                        placeholder={t('customerManagement.position')}
                        value={values?.customerInfo.position ?? ''}
                        onChange={handleChange}
                        isInvalid={
                          touched.customerInfo?.position &&
                          !!errors.customerInfo?.position
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                    <Form.Group
                      controlId="customerInfo.company"
                      className="form-inline form-inline-dealer"
                    >
                      <Form.Label className="col-sm-3 mr-20 text-left ml-20">
                        {t('customerManagement.company')}
                      </Form.Label>
                      <Form.Control
                        className="col-sm-7 form-control-bordered margin-input"
                        name="customerInfo.company"
                        placeholder={t('customerManagement.company')}
                        value={values?.customerInfo.company ?? ''}
                        onChange={handleChange}
                        isInvalid={
                          touched.customerInfo?.company &&
                          !!errors.customerInfo?.company
                        }
                        disabled={isDisabled}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} />
                </Row>
                <Accordion>
                  <Card>
                    <Card.Header>
                      <Accordion.Toggle
                        as={Button}
                        variant="button"
                        eventKey="0"
                        style={{
                          backgroundColor: 'lavender',
                          width: '100%',
                        }}
                      >
                        {t('customerManagement.contactLabel')}
                      </Accordion.Toggle>
                    </Card.Header>
                    <Accordion.Collapse eventKey="0">
                      <Card.Body>
                        <Form.Group
                          controlId="customerInfo.contactInformation.address1"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.address1')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.address1"
                            placeholder={t('dealerManagement.contact.address1')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.address1 ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.address2"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.address2')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.address2"
                            placeholder={t('dealerManagement.contact.address2')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.address2 ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.address3"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.address3')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.address3"
                            placeholder={t('dealerManagement.contact.address3')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.address3 ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.zipCode"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.zipCode')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.zipCode"
                            placeholder={t('dealerManagement.contact.zipCode')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.zipCode ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.city"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.city')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.city"
                            placeholder={t('dealerManagement.contact.city')}
                            value={
                              values?.customerInfo.contactInformation?.city ??
                              ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.district"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.district')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.district"
                            placeholder={t('dealerManagement.contact.district')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.district ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.state"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.state')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.state"
                            placeholder={t('dealerManagement.contact.state')}
                            value={
                              values?.customerInfo.contactInformation?.state ??
                              ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.country"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.country')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.country"
                            placeholder={t('dealerManagement.contact.country')}
                            value={
                              values?.customerInfo.contactInformation
                                ?.country ?? ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.phone"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.phone')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.phone"
                            placeholder={t('dealerManagement.contact.phone')}
                            value={
                              values?.customerInfo.contactInformation?.phone ??
                              ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.email"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.email')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.email"
                            placeholder={t('dealerManagement.contact.email')}
                            value={
                              values?.customerInfo.contactInformation?.email ??
                              ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                        <Form.Group
                          controlId="customerInfo.contactInformation.webUrl"
                          className="form-inline form-inline-dealer"
                        >
                          <Form.Label className="col-sm-3 mr-20 text-left">
                            {t('dealerManagement.contact.webUrl')}
                          </Form.Label>
                          <Form.Control
                            className="col-sm-6 form-control-bordered ml-10"
                            name="customerInfo.contactInformation.webUrl"
                            placeholder={t('dealerManagement.contact.webUrl')}
                            value={
                              values?.customerInfo.contactInformation?.webUrl ??
                              ''
                            }
                            onChange={handleChange}
                            disabled={isDisabled}
                          />
                        </Form.Group>
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
                <Row className="mt-8">
                  <Col md={12} className="d-flex justify-content-end">
                    {showExtendedButton && handleBack && (
                      <Button
                        dimension="lg"
                        onClick={handleBack}
                        className="search-btn button-customer"
                      >
                        {t('goBack')}
                      </Button>
                    )}
                    <Button
                      dimension="lg"
                      type="submit"
                      className="search-btn button-customer ml-3"
                      disabled={
                        isDisabled ||
                        (rootDealer &&
                          !rootDealer?.permissions?.subDealerAllowed &&
                          !values?.dealerInfo?.dealerId)
                      }
                    >
                      {t('dealerManagement.buttonSave')}
                    </Button>
                    {showExtendedButton && (
                      <Button
                        dimension="lg"
                        type="submit"
                        onClick={() => {
                          if (setSaveAndSelect) {
                            setSaveAndSelect(true);
                          }
                        }}
                        className="search-btn button-customer ml-3"
                        disabled={
                          isDisabled ||
                          (rootDealer &&
                            !rootDealer?.permissions?.subDealerAllowed &&
                            !values?.dealerInfo?.dealerId)
                        }
                      >
                        {t('dealerManagement.buttonSaveAndSelect')}
                      </Button>
                    )}
                  </Col>
                  <Col md={1} className="visibility-hidden" />
                </Row>
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
};

DealerCustomersDetailView.defaultProps = {
  rootDealer: undefined,
  showExtendedButton: false,
  setSaveAndSelect: () => {},
  handleBack: undefined,
};
